import { RuntimeWidget } from 'client/types';
import withErrorBoundary from 'client/widget-components/components/ErrorBoundary/withErrorBoundary';
import { useEditor } from 'client/widget-components/customHooks/useEditor';
import React from 'react';
import BookerStep from './components/BookerStep';
import ContainerQueryStyles from './components/ContainerQueryStyles';
import SelectEventsStep from './components/SelectEventsStep';
import { BookerContextProvider } from './Context';
import { useResolveUrlProps } from './hooks/useResolveUrlProps';
import { useRuntimeCalBookingState } from './hooks/useRuntimeCalBookingState';
import { logBookingInfo } from './loggingUtils';
import { CalBookingWrapper } from './styles';
import {
    BookerContextType,
    CalBookingProps,
    DesignStyle,
    PricingType,
    WidgetStep,
} from './types';
import { isBookingDryRun as isBookingDryRunUtil } from './utils';

const STEP_COMPONENTS = {
    [WidgetStep.EVENT_SELECTION]: SelectEventsStep,
    [WidgetStep.BOOKER]: BookerStep,
};

const RuntimeCalBooking: RuntimeWidget<CalBookingProps> = (
    props: CalBookingProps
) => {
    const {
        clientId,
        featureMetadata,
        widgetId,
        organizationId,
        priceInfo,
        eventIdentifier,
        events,
        designStyle: designStyleProp,
        groupedAppointmentInfo,
    } = props;

    const [state, actions] = useRuntimeCalBookingState(props);
    const editor = useEditor();
    const isBookingDryRun = isBookingDryRunUtil(editor, props.featureMetadata);

    useResolveUrlProps({
        ...props,
        isBookingDryRun,
        setRescheduleUid: actions.setRescheduleUid,
        setCancelBookingId: actions.setCancelBookingId,
        setEventSlug: actions.setEventSlug,
        setTeamId: actions.setTeamId,
        setUserName: actions.setUserName,
    });

    // backward compatibility with single event booking
    const isBookingActive = props?.events?.length >= 1 || !!state.eventSlug;
    const designStyle = designStyleProp || DesignStyle.SITE_THEME;

    const bookerContext: BookerContextType = {
        ...priceInfo,
        ...featureMetadata,
        eventSlug: state.eventSlug,
        teamId: state.teamId,
        userName: state.userName,
        rescheduleUid: state.rescheduleUid,
        cancelBookingId: state.cancelBookingId,
        widgetId,
        designStyle,
        language: state.language,
        apiUrl: state.apiUrl,
        clientId,
        organizationId,
        pricingType: priceInfo?.pricingType || PricingType.FREE,
        eventIdentifier,
        isBookingActive,
        isBookingDryRun,
        events,
        groupedAppointmentInfo,
    };

    if (!isBookingActive && !editor.isInPreviewOrEditor()) {
        logBookingInfo(
            'Trying to render RuntimeCalBooking when booking is not active in non-editor mode',
            {
                additionalInfoToLog: bookerContext,
                isInPreviewOrEditor: editor.isInPreviewOrEditor(),
            }
        );
        return null;
    }

    const isMultipleEvents = events && events.length > 1;
    const currentEventSlug = state.eventSlug;
    const currentStep =
        isMultipleEvents && !currentEventSlug
            ? WidgetStep.EVENT_SELECTION
            : WidgetStep.BOOKER;

    const CurrentStepComponent = STEP_COMPONENTS[currentStep];

    return (
        <CalBookingWrapper
            data-auto='ssr-cal-booking-widget'
            className={`booking-wrapper ${designStyle}`}
        >
            <BookerContextProvider
                {...bookerContext}
                state={state}
                actions={actions}
            >
                <ContainerQueryStyles widgetId={widgetId} />
                <CurrentStepComponent />
            </BookerContextProvider>
        </CalBookingWrapper>
    );
};

export default withErrorBoundary({
    Comp: RuntimeCalBooking,
    logProps: true,
    componentName: 'CalBooking',
    additionalInfoToLog: {
        tag: 'booking',
    },
});
